import { useState, useEffect, useCallback, useContext } from "react";
import { supabase } from "../supabase";
import { CartContext, AdminContext, BrandContext } from "../contexts";
import { AddToCartBtn, Btn, HeartIcon } from "../components/ui";
import ShareButton from "../components/ShareButton";
import { getPrimaryImage } from "../utils/helpers";
import { POPULAR_CANDIDATES, selectPopularProducts } from "../utils/popularProducts";
import { VARIANT_MEMBER_IDS, familyFor, headIdFor, memberIds, valuesForVariant, variantForValues } from "../utils/variantFamilies";
import { useFavorites } from "../utils/favorites";


// Variant families: one card, a dropdown, several purchasable product rows.
// Declared EXPLICITLY, never inferred from names - "Methylene Blue" and
// "Methylene Blue (Tablets)" share a name prefix but are different products,
// not sizes of one product.
// head = the product whose card is shown; members = every buyable option.
export const ProductCard = ({ p, onClick, inventoryManagement, allProducts = [] }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const fam = familyFor(p.id);
  // Every card gets a selector, so a single-variant product still states its dose
  // somewhere now that the images carry only the compound name.
  const options = fam
    ? memberIds(fam).map(id => allProducts.find(x => x.id === id)).filter(Boolean)
    : [p];
  const firstSellable = options.find(o => o.stock == null || o.stock > 0);
  const [variantId, setVariantId] = useState((firstSellable || p).id);
  // Fall back to the head product if the chosen variant vanished (stock/visibility change).
  const selected = options.find(o => o.id === variantId) || p;
  const saved = isFavorite(p.id);
  const displayName = fam ? fam.label : selected.name;
  const axisValues = fam?.axes ? (valuesForVariant(fam, selected.id) || valuesForVariant(fam, p.id)) : null;
  const chooseAxisValue = (axisIndex, value) => {
    const nextValues = axisValues.map((current, index) => index === axisIndex ? value : current);
    const variant = variantForValues(fam, nextValues);
    if (variant) setVariantId(variant.id);
  };

  return (
  <div className="product-card">
    <div onClick={onClick} style={{ cursor: "pointer" }}>
      <div className="card-img" style={{ aspectRatio: "1 / 1", position: "relative", background: "var(--surface2)" }}>
        <img src={getPrimaryImage(selected)} alt={fam ? fam.label : selected.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <button type="button" aria-label={`${saved ? "Remove" : "Save"} ${displayName} ${saved ? "from" : "to"} favorites`} aria-pressed={saved} onClick={event => { event.stopPropagation(); toggleFavorite(p.id); }} style={{
          position: "absolute", top: 10, left: 10, zIndex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 32, height: 32, padding: 0, cursor: "pointer", background: "rgba(8,8,16,.72)", color: saved ? "var(--red)" : "var(--text)",
          border: `1px solid ${saved ? "var(--red)" : "var(--border)"}`, borderRadius: 16,
        }}><HeartIcon filled={saved} /></button>
        <ShareButton product={selected} variant="icon" />
      </div>
      <div style={{ padding: "16px 18px 8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.1em", margin: 0, textTransform: "uppercase" }}>{p.category} · {selected.sku}</p>
          {selected.stock != null && selected.stock <= 0
            ? <span style={{ background: "var(--red)", color: "white", fontSize: "10px", fontFamily: "var(--font-mono)", padding: "4px 8px", borderRadius: 3, fontWeight: 700, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>SOLD OUT</span>
            : selected.stock <= 5 && selected.stock > 0 && <span style={{ background: "rgba(232,160,32,0.9)", color: "#111", fontSize: "10px", fontFamily: "var(--font-mono)", padding: "4px 8px", borderRadius: 3, fontWeight: 700, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>LOW STOCK</span>}
        </div>
        <h3 style={{ fontSize: "17px", fontFamily: "var(--font-display)", marginBottom: 4 }}>{fam ? fam.label : selected.name}</h3>
      </div>
    </div>
    {fam?.axes ? (
      <div style={{ padding: "0 18px 10px" }}>
        {fam.axes.map((axis, axisIndex) => (
          <div key={axis.name} style={axisIndex > 0 ? { marginTop: 10 } : undefined}>
            <label htmlFor={`variant-${p.id}-${axis.name}`} style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{axis.name}</label>
            <select id={`variant-${p.id}-${axis.name}`} value={axisValues[axisIndex]} onChange={e => chooseAxisValue(axisIndex, e.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: "13px" }}>
              {axis.values.map(value => {
                const values = axisValues.map((current, index) => index === axisIndex ? value : current);
                const row = options.find(option => option.id === variantForValues(fam, values)?.id);
                const soldOut = row && row.stock != null && row.stock <= 0;
                return <option key={value} value={value} disabled={axis.name === "Size" && soldOut}>{value}{axis.name === "Size" && row ? (soldOut ? " - sold out" : ` - $${row.price.toFixed(2)}`) : ""}</option>;
              })}
            </select>
          </div>
        ))}
      </div>
    ) : options.length > 0 && (
      <div style={{ padding: "0 18px 10px" }}>
        <label htmlFor={`variant-${p.id}`} style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Size</label>
        <select
          id={`variant-${p.id}`}
          value={selected.id}
          onChange={e => setVariantId(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: "13px" }}
        >
          {options.map(o => (
            <option key={o.id} value={o.id} disabled={o.stock != null && o.stock <= 0}>
              {(o.subtitle || o.name)}{o.stock != null && o.stock <= 0 ? " - sold out" : ` - $${o.price.toFixed(2)}`}
            </option>
          ))}
        </select>
      </div>
    )}
    <div style={{ padding: "0 18px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: "18px", color: "var(--amber)", fontWeight: 500 }}>${selected.price.toFixed(2)}</span>
      <AddToCartBtn product={selected} />
    </div>
  </div>
  );
};

const SkeletonCard = () => (
  <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
    <div className="skeleton" style={{ aspectRatio: "4 / 5" }} />
    <div style={{ padding: "16px 18px" }}>
      <div className="skeleton" style={{ height: 10, width: "40%", marginBottom: 8 }} />
      <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 12 }} />
      <div className="skeleton" style={{ height: 14, width: "30%" }} />
    </div>
  </div>
);

const ShopView = ({ setView, setSelectedProduct, products, setProducts }) => {
  const { favoriteIds } = useFavorites();
  const { dispatch } = useContext(CartContext);
  const { inventoryManagement } = useContext(AdminContext);
  const b = useContext(BrandContext);
  const [productsLoading, setProductsLoading] = useState(true);
  // Distinguishes "the request failed" from "the request succeeded and there
  // just isn't anything" (issue #140) — without this, a failed load left
  // products empty and rendered the same "no products" state as a genuinely
  // empty catalog, telling a shopper the shop is empty when the load broke.
  const [productsError, setProductsError] = useState(false);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [topSellerIds, setTopSellerIds] = useState([]);

  const loadProducts = useCallback(() => {
    setProductsLoading(true);
    setProductsError(false);
    return supabase
      .from("products")
      .select("*")
      .neq("is_visible", false)
      .order("name")
      .then(({ data, error }) => {
        if (error) {
          setProductsError(true);
        } else if (data?.length > 0) {
          setProducts(data);
          dispatch({ type: "RECONCILE", products: data, inventoryManagement });
        }
        setProductsLoading(false);
      });
  }, [inventoryManagement, dispatch, setProducts]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // "Popular" line — top 3 IN-STOCK products by units sold (read-only top_selling_products RPC).
  // Ask for more candidates than we render: out-of-stock sellers are filtered out below, so
  // requesting exactly 3 renders fewer than 3 whenever a top seller is sold out.
  useEffect(() => {
    supabase.rpc("top_selling_products", { n: POPULAR_CANDIDATES }).then(({ data, error }) => {
      if (!error && Array.isArray(data)) setTopSellerIds(data.map(r => r.product_id));
    });
  }, []);

  const cats = ["All", "Favorites", ...new Set(products.map(p => p.category))];
  // Count only favorites that are on the shop right now (a hidden product stays saved but is not shown).
  const favoriteCount = products.filter(p => !VARIANT_MEMBER_IDS.has(p.id) && favoriteIds.has(headIdFor(p.id))).length;
  const filtered = products.filter(p =>
    // Non-head variant members are bought through their family's dropdown, not
    // their own card. Without this, GLP-3 20mg would appear twice.
    !VARIANT_MEMBER_IDS.has(p.id) &&
    (filter === "All" || (filter === "Favorites" ? favoriteIds.has(headIdFor(p.id)) : p.category === filter)) &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );
  // Out-of-stock items drop to their own section below; users open them to set restock alerts.
  const anySellable = p => {
    const f = familyFor(p.id);
    const pool = f ? memberIds(f).map(id => products.find(x => x.id === id)).filter(Boolean) : [p];
    return pool.some(o => o.stock == null || o.stock > 0);
  };
  const inStock = filtered.filter(anySellable);
  const outOfStock = filtered.filter(p => !anySellable(p));

  if (productsLoading) return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 24px" }}>
      <div style={{ marginBottom: 48 }}>
        <div className="skeleton" style={{ height: 12, width: 120, marginBottom: 12 }} />
        <div className="skeleton" style={{ height: 48, width: "60%", marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 16, width: "40%" }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 24 }}>
        {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 24px" }}>
      {/* Store policy banners */}
      <div className="fade-up" style={{ marginBottom: 32, textAlign: "center", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
      {b.freeShippingEnabled !== false && b.freeShippingThreshold > 0 && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.15)",
            borderRadius: "20px", padding: "10px 20px",
            fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--text-dim)", letterSpacing: "0.04em",
          }}>
            <span style={{ color: "var(--amber)" }}>✦</span>
            Free shipping on orders above <span style={{ color: "var(--amber)", fontWeight: 700, marginLeft: 4 }}>${b.freeShippingThreshold}</span>
          </div>
      )}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.15)", borderRadius: 20, padding: "10px 20px", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text-dim)", letterSpacing: "0.04em" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6M10 3v4.5L5.5 16a3 3 0 0 0 2.6 4.5h7.8a3 3 0 0 0 2.6-4.5L14 7.5V3"/><path d="M8 14h8"/></svg>
          <span>Any 10 vials <span style={{ color: "var(--amber)", fontWeight: 700 }}>save 20%</span> · mix and match</span>
        </div>
      </div>

      {/* Hero */}
      <div className="fade-up" style={{ marginBottom: 48 }}>
        <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.15em", color: "var(--amber)", textTransform: "uppercase", marginBottom: 8 }}>{b.shopEyebrow}</p>
        <h1 style={{ fontSize: "clamp(36px, 6vw, 64px)", lineHeight: 1.05, letterSpacing: "-0.02em", marginBottom: 16 }}>{b.tagline.replace(b.taglineEm, "").trimEnd()}<br /><em style={{ color: "var(--amber)" }}>{b.taglineEm}</em></h1>
        <p style={{ color: "var(--text-dim)", maxWidth: 480, fontSize: "16px", lineHeight: 1.6 }}>{b.shopDescription}</p>
      </div>

      {/* Popular products — top sellers that are in stock (hidden until sales data loads) */}
      {(() => {
        const popular = selectPopularProducts(topSellerIds, products, {
          headIdFor,
          isSellable: anySellable,
        });
        return filter !== "Favorites" && popular.length > 0 && (
          <div className="fade-up-2" style={{ marginBottom: 40 }}>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--amber)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14 }}>Popular</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }} className="r-grid">
              {popular.map(p => (
                <ProductCard key={p.id} p={p} allProducts={products} inventoryManagement={inventoryManagement}
                  onClick={() => { setSelectedProduct(p); setView("product", { productId: p.id }); }} />
              ))}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="fade-up-2" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28, alignItems: "center" }}>
        <div style={{ position: "relative", width: 220 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…" style={{ paddingLeft: 34 }} />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "14px", pointerEvents: "none" }}>⌕</span>
        </div>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} className="btn-interactive" style={{
            padding: "8px 16px", borderRadius: "4px", fontSize: "13px", fontWeight: 600,
            background: filter === c ? "var(--amber)" : "transparent",
            color: filter === c ? "#111" : "var(--text-dim)",
            border: `1.5px solid ${filter === c ? "var(--amber)" : "var(--border)"}`,
            transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
          }}>{c === "Favorites" && <><HeartIcon filled={filter === "Favorites"} />&nbsp;</>}{c}{c === "Favorites" && ` (${favoriteCount})`}</button>
        ))}
      </div>

      {/* Grid (in-stock) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 24 }}>
        {inStock.map((p, i) => (
          <div key={p.id} style={{ animation: `fadeUp 0.4s ${Math.min(i * 0.04, 0.3)}s cubic-bezier(0.16, 1, 0.3, 1) both` }}>
            <ProductCard p={p} allProducts={products} inventoryManagement={inventoryManagement}
              onClick={() => { setSelectedProduct(p); setView("product", { productId: p.id }); }} />
          </div>
        ))}
      </div>

      {/* Out of stock — dropped below; open a product to set up a restock notification */}
      {outOfStock.length > 0 && (
        <div style={{ marginTop: 48 }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>Out of Stock</p>
          <p style={{ color: "var(--text-dim)", fontSize: "13px", marginBottom: 18 }}>Open a product to get notified when it's back in stock.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 24, opacity: 0.72 }}>
            {outOfStock.map((p, i) => (
              <div key={p.id} style={{ animation: `fadeUp 0.4s ${Math.min(i * 0.04, 0.3)}s cubic-bezier(0.16, 1, 0.3, 1) both` }}>
                <ProductCard p={p} allProducts={products} inventoryManagement={inventoryManagement}
                  onClick={() => { setSelectedProduct(p); setView("product", { productId: p.id }); }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {productsError ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "13px", letterSpacing: "0.08em", color: "var(--red)" }}>COULD NOT LOAD THE CATALOG</p>
          <p style={{ fontSize: "12.5px", marginTop: 8, color: "var(--text-muted)" }}>Something went wrong at our end, not yours. The products are still there.</p>
          <div style={{ marginTop: 14 }}>
            <Btn small onClick={loadProducts}>Try again</Btn>
          </div>
        </div>
      ) : filtered.length === 0 && (filter === "Favorites" && !search ? (
        <div style={{ border: "1px dashed var(--border)", borderRadius: 6, padding: 28, color: "var(--text-dim)", textAlign: "center", maxWidth: 600, marginTop: 16 }}>
          No favorites yet. Tap the ♡ on any product to save it here.
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "13px", letterSpacing: "0.08em" }}>No products match your search.</p>
        </div>
      ))}
    </div>
  );
};

export default ShopView;
